import React, { useState, useEffect, createContext, useContext } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

// Types
interface User {
  id: number;
  username: string;
  role: "admin" | "user";
  is_active: boolean;
}

interface Movie {
  id: number;
  filename: string;
  title: string | null;
  storage: string;
  description: string | null;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string, user: User) => void;
  logout: () => void;
}

// Auth Context
const AuthContext = createContext<AuthContextType | null>(null);

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}

// API helpers
async function api<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(endpoint, { ...options, headers });
  return res.json();
}

// Login Page Component
function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    // Check if setup is needed
    api<{ requiresSetup: boolean }>("/api/auth/status").then((res) => {
      setNeedsSetup(res.requiresSetup);
      if (res.requiresSetup) setIsRegister(true);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const endpoint = isRegister ? "/api/auth/register" : "/api/auth/login";
      const res = await api<{
        success: boolean;
        token?: string;
        error?: string;
        message?: string;
        user?: User;
      }>(endpoint, {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });

      if (res.success && res.token && res.user) {
        login(res.token, res.user);
      } else if (res.success && !res.token) {
        // Registration without token (waiting for activation)
        setSuccess(res.message || "Registrazione completata. Attendi l'attivazione.");
        setIsRegister(false);
      } else {
        setError(res.error || "Errore sconosciuto");
      }
    } catch (err) {
      setError("Errore di connessione al server");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Movie Box</h1>
        <p className="subtitle">
          {needsSetup
            ? "Crea il primo account admin"
            : isRegister
            ? "Registrazione"
            : "Accedi al pannello admin"}
        </p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete={isRegister ? "new-password" : "current-password"}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Caricamento..." : isRegister ? "Registrati" : "Accedi"}
          </button>
        </form>

        {!needsSetup && (
          <div className="toggle-link">
            <button onClick={() => setIsRegister(!isRegister)}>
              {isRegister ? "Hai già un account? Accedi" : "Non hai un account? Registrati"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Dashboard Components
function Sidebar({ currentPage, setCurrentPage }: { currentPage: string; setCurrentPage: (p: string) => void }) {
  const { user, logout } = useAuth();

  return (
    <aside className="sidebar">
      <h1>Movie Box</h1>
      <nav>
        <a
          href="#"
          className={currentPage === "movies" ? "active" : ""}
          onClick={(e) => { e.preventDefault(); setCurrentPage("movies"); }}
        >
          Film
        </a>
        <a
          href="#"
          className={currentPage === "users" ? "active" : ""}
          onClick={(e) => { e.preventDefault(); setCurrentPage("users"); }}
        >
          Utenti
        </a>
      </nav>
      <div className="user-info">
        <div className="username">{user?.username}</div>
        <div className="role">{user?.role === "admin" ? "Amministratore" : "Utente"}</div>
        <button className="logout-btn" onClick={logout}>Logout</button>
      </div>
    </aside>
  );
}

function MoviesPage() {
  const [movies, setMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [storageFilter, setStorageFilter] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadMovies();
  }, []);

  const loadMovies = async () => {
    setLoading(true);
    try {
      const res = await api<{ success: boolean; data: Movie[] }>("/api/movies");
      if (res.success) {
        setMovies(res.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const storages = [...new Set(movies.map((m) => m.storage))].sort();

  const filteredMovies = movies.filter((m) => {
    const matchesSearch =
      m.filename.toLowerCase().includes(search.toLowerCase()) ||
      m.title?.toLowerCase().includes(search.toLowerCase()) ||
      m.storage.toLowerCase().includes(search.toLowerCase());
    const matchesStorage = storageFilter === "" || m.storage === storageFilter;
    return matchesSearch && matchesStorage;
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, storageFilter, itemsPerPage]);

  // Pagination
  const totalPages = Math.ceil(filteredMovies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMovies = filteredMovies.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div>
      <div className="page-header">
        <h2>Film nel catalogo</h2>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="label">Totale Film</div>
          <div className="value">{movies.length}</div>
        </div>
        <div className="stat-card">
          <div className="label">Storage</div>
          <div className="value">{storages.length}</div>
        </div>
        <div className="stat-card">
          <div className="label">Filtrati</div>
          <div className="value">{filteredMovies.length}</div>
        </div>
      </div>

      <div className="filters-row">
        <div className="search-box">
          <input
            type="text"
            placeholder="Cerca film..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Storage:</label>
          <select value={storageFilter} onChange={(e) => setStorageFilter(e.target.value)}>
            <option value="">Tutti</option>
            {storages.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Per pagina:</label>
          <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Caricamento...</div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Titolo</th>
                  <th>Storage</th>
                  <th>Aggiunto il</th>
                </tr>
              </thead>
              <tbody>
                {paginatedMovies.map((movie) => (
                  <tr key={movie.id}>
                    <td>{movie.filename}</td>
                    <td>{movie.title || "-"}</td>
                    <td>{movie.storage}</td>
                    <td>{new Date(movie.created_at).toLocaleDateString("it-IT")}</td>
                  </tr>
                ))}
                {paginatedMovies.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center" }}>
                      Nessun film trovato
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-small"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                &laquo;
              </button>
              <button
                className="btn btn-small"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &lsaquo;
              </button>
              <span className="page-info">
                Pagina {currentPage} di {totalPages}
              </span>
              <button
                className="btn btn-small"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &rsaquo;
              </button>
              <button
                className="btn btn-small"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                &raquo;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function UsersPage() {
  const [users, setUsers] = useState<(User & { created_at: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<(User & { created_at: string }) | null>(null);
  const [editForm, setEditForm] = useState({ username: "", password: "" });
  const [editError, setEditError] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await api<{ success: boolean; data: (User & { created_at: string })[] }>("/api/auth/users");
      if (res.success) {
        setUsers(res.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (id: number, isActive: boolean) => {
    await api(`/api/auth/users/${id}/activate`, {
      method: "PUT",
      body: JSON.stringify({ is_active: !isActive }),
    });
    loadUsers();
  };

  const toggleRole = async (id: number, currentRole: string) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    await api(`/api/auth/users/${id}/role`, {
      method: "PUT",
      body: JSON.stringify({ role: newRole }),
    });
    loadUsers();
  };

  const deleteUserHandler = async (id: number, username: string) => {
    if (!confirm(`Sei sicuro di voler eliminare l'utente "${username}"?`)) return;
    await api(`/api/auth/users/${id}`, { method: "DELETE" });
    loadUsers();
  };

  const openEditModal = (user: User & { created_at: string }) => {
    setEditingUser(user);
    setEditForm({ username: user.username, password: "" });
    setEditError("");
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditForm({ username: "", password: "" });
    setEditError("");
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    setEditError("");
    setEditLoading(true);

    try {
      const updateData: { username?: string; password?: string } = {};

      if (editForm.username !== editingUser.username) {
        updateData.username = editForm.username;
      }
      if (editForm.password) {
        updateData.password = editForm.password;
      }

      if (Object.keys(updateData).length === 0) {
        closeEditModal();
        return;
      }

      const res = await api<{ success: boolean; error?: string }>(`/api/auth/users/${editingUser.id}`, {
        method: "PUT",
        body: JSON.stringify(updateData),
      });

      if (res.success) {
        closeEditModal();
        loadUsers();
      } else {
        setEditError(res.error || "Errore durante l'aggiornamento");
      }
    } catch {
      setEditError("Errore di connessione");
    } finally {
      setEditLoading(false);
    }
  };

  const pendingUsers = users.filter((u) => !u.is_active);

  return (
    <div>
      <div className="page-header">
        <h2>Gestione Utenti</h2>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="label">Totale Utenti</div>
          <div className="value">{users.length}</div>
        </div>
        <div className="stat-card">
          <div className="label">In attesa di attivazione</div>
          <div className="value">{pendingUsers.length}</div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Caricamento...</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Ruolo</th>
                <th>Stato</th>
                <th>Registrato il</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    {user.username}
                    {user.id === currentUser?.id && " (tu)"}
                  </td>
                  <td>
                    <span className={`badge badge-${user.role}`}>
                      {user.role === "admin" ? "Admin" : "Utente"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge-${user.is_active ? "active" : "inactive"}`}>
                      {user.is_active ? "Attivo" : "In attesa"}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="actions">
                    <button
                      className="btn btn-small"
                      onClick={() => openEditModal(user)}
                    >
                      Modifica
                    </button>
                    {user.id !== currentUser?.id && (
                      <>
                        <button
                          className={`btn btn-small ${user.is_active ? "btn-warning" : "btn-success"}`}
                          onClick={() => toggleActive(user.id, user.is_active)}
                        >
                          {user.is_active ? "Disattiva" : "Attiva"}
                        </button>
                        <button
                          className="btn btn-small btn-primary"
                          onClick={() => toggleRole(user.id, user.role)}
                        >
                          {user.role === "admin" ? "Rendi User" : "Rendi Admin"}
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => deleteUserHandler(user.id, user.username)}
                        >
                          Elimina
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Modifica Utente</h3>
              <button className="modal-close" onClick={closeEditModal}>&times;</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              {editError && <div className="error-message">{editError}</div>}
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  minLength={3}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nuova Password (lascia vuoto per non modificare)</label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  minLength={6}
                  placeholder="Minimo 6 caratteri"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn" onClick={closeEditModal}>
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary" disabled={editLoading}>
                  {editLoading ? "Salvataggio..." : "Salva"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const [currentPage, setCurrentPage] = useState("movies");

  return (
    <div className="dashboard">
      <Sidebar currentPage={currentPage} setCurrentPage={setCurrentPage} />
      <main className="main-content">
        {currentPage === "movies" && <MoviesPage />}
        {currentPage === "users" && <UsersPage />}
      </main>
    </div>
  );
}

// Main App
function App() {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const savedToken = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        // Only allow admin users in web UI
        if (parsedUser.role === "admin") {
          setToken(savedToken);
          setUser(parsedUser);
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      } catch {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  }, []);

  const login = (newToken: string, newUser: User) => {
    // Only allow admin users
    if (newUser.role !== "admin") {
      alert("Accesso riservato agli amministratori");
      return;
    }
    localStorage.setItem("token", newToken);
    localStorage.setItem("user", JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return <div className="loading">Caricamento...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      {user && token ? <Dashboard /> : <LoginPage />}
    </AuthContext.Provider>
  );
}

// Render
const root = createRoot(document.getElementById("root")!);
root.render(<App />);
